import CancelToken from 'axios/lib/cancel/CancelToken';
import isCancel from 'axios/lib/cancel/isCancel';
import { AxiosESTransport } from 'searchkit';

import { OWNED_FILTERS_PARAM, OWNED_FILTERS_QUERY_KEY } from 'constants/search';
import { sendEvent } from 'services/analytics';

// How long to wait before each retry of a search the network swallowed.
const RETRY_DELAYS_MS = [500, 1500];

/**
 * Moves the ownership marker the query processor leaves on the query object out of the body and
 * onto the request URL. ElasticSearch answers an unrecognised top-level body key with a
 * parsing_exception, so the marker must not survive past this point; a query-string parameter also
 * degrades safely in both directions, since an old server ignores it and a new server reads no
 * marker from an old client.
 *
 * Also keeps the newest search alive on a poor connection: the search before it is cancelled, and
 * a request that got no answer from the server is retried.
 */
class OwnedFiltersTransport extends AxiosESTransport {
  /**
   * @override
   * @param  {Object} query
   * @return {Promise}
   */
  search(query) {
    const { [OWNED_FILTERS_QUERY_KEY]: owned, ...body } = query || {};
    const path =
      Array.isArray(owned) && owned.length
        ? `${this.options.searchUrlPath}?${OWNED_FILTERS_PARAM}=${encodeURIComponent(
            owned.join(','),
          )}`
        : this.options.searchUrlPath;

    // Only the newest search can reach the screen. An older one still running is just competing
    // with it for the connection, and SearchRequest ignores the rejection cancelling it causes.
    if (this.cancelPreviousSearch) {
      this.cancelPreviousSearch();
    }
    const { token, cancel } = CancelToken.source();
    this.cancelPreviousSearch = cancel;

    return this.post(path, body, token);
  }

  /**
   * @param  {String} path
   * @param  {Object} body
   * @param  {CancelToken} cancelToken
   * @param  {Number} attempt
   * @return {Promise}
   */
  post(path, body, cancelToken, attempt = 0) {
    return this.axios
      .post(path, body, { cancelToken })
      .then(this.getData)
      .catch(error => {
        // A response means the server did answer, so asking again would not change that answer.
        if (isCancel(error) || error.response || attempt >= RETRY_DELAYS_MS.length) {
          throw error;
        }
        sendEvent({ event: 'Search.Retry', attempt: attempt + 1, reason: error.code || 'network' });
        return new Promise(resolve => setTimeout(resolve, RETRY_DELAYS_MS[attempt])).then(() => {
          cancelToken.throwIfRequested();
          return this.post(path, body, cancelToken, attempt + 1);
        });
      });
  }
}

export default OwnedFiltersTransport;
